// btw-front/src/pages/AdminDealEdit.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message, Modal } from "antd";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import AdminDealForm, { emptyForm, quarterFromDate } from "../components/admin/AdminDealForm";
import DealChangeHistory from "../components/admin/DealChangeHistory";
import DealOwnerChangeModal from "../components/admin/DealOwnerChangeModal";
import {
  get as getAdminDeal,
  update as updateAdminDeal,
  remove as removeAdminDeal,
  adopt as adoptAdminDeal,
  uploadAdoptionFiles,
  removeAdoptionFile,
  downloadAdoptionFile,
} from "../api/adminDeals";
import AdoptionFilePicker from "../components/deal/AdoptionFilePicker";
import AdoptionFilesSection from "../components/deal/AdoptionFilesSection";
import { SALES_STAGES } from "../constants/dealStatus";
import { usePartnerSession } from "../context/PartnerSessionContext";
import { goBackOr } from "../lib/nav";

function dealToForm(d) {
  return {
    projectName: d.projectName || "",
    customerCompany: d.customerCompany || "",
    customerBusinessNumber: d.customerBusinessNumber || "",
    customerContactName: d.customerContactName || "",
    customerContactPosition: d.customerContactPosition || "",
    customerPhone: d.customerPhone || "",
    customerEmail: d.customerEmail || "",
    adminSalesRepId: d.adminSalesRepId || "",
    poExpectedDate: d.poExpectedDate ? String(d.poExpectedDate).slice(0, 10) : "",
    productDetail: d.productDetail || "",
    poAmount: Number(d.poAmount || 0),
    vatExcluded: !!d.vatExcluded,
    salesProgress: d.salesProgress || "",
    channel: d.channel || "",
    pocRequired: d.pocRequired || "undecided",
    brochureDelivered: !!d.brochureDelivered,
  };
}

export default function AdminDealEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile } = usePartnerSession();

  const [form, setForm] = useState(() => emptyForm());
  const [deal, setDeal] = useState(null);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // '도입' 전환 다이얼로그 — 증빙 파일 ≥1 필수(기존 파일 있으면 생략 가능)
  const [adoptionOpen, setAdoptionOpen] = useState(false);
  const [adoptionFilesPending, setAdoptionFilesPending] = useState([]);
  const [adoptionSubmitting, setAdoptionSubmitting] = useState(false);

  // 목록으로 복귀 — 온 곳(관리자 대시보드의 해당 탭)으로 되돌아감. 없으면 /admin.
  const backToList = useCallback(() => goBackOr(navigate, "/admin"), [navigate]);

  // 영업단계 변경 — 파트너 딜 상세(PartnerDealDetail)와 동일 UX: 버튼 클릭 →
  // confirm → PUT {status} 낙관적 업데이트 + 실패 시 롤백. 폼 제출과 독립 동작.
  function handleStatusChange(nextStatus) {
    if (!deal || deal.status === nextStatus) return;
    // '도입' 은 증빙 파일 업로드가 필수 — 전용 다이얼로그(PartnerDealDetail 과 동일 UX)
    if (nextStatus === "adoption") {
      setAdoptionFilesPending([]);
      setAdoptionOpen(true);
      return;
    }
    Modal.confirm({
      title: t("partners.deals.detail.statusChangeConfirmTitle"),
      content: t("partners.deals.detail.statusChangeConfirmContent", {
        from: t(`partners.deals.status.${deal.status}`),
        to: t(`partners.deals.status.${nextStatus}`),
      }),
      okText: t("partners.deals.detail.statusChangeConfirmOk"),
      cancelText: t("partners.deals.detail.statusChangeConfirmCancel"),
      onOk: async () => {
        const prevStatus = deal.status;
        setDeal((d) => ({ ...d, status: nextStatus }));
        try {
          await updateAdminDeal(id, { status: nextStatus });
          message.success(t("partners.deals.detail.statusChanged"));
        } catch (err) {
          setDeal((d) => ({ ...d, status: prevStatus }));
          const msg =
            (err && err.payload && err.payload.error && err.payload.error.message) ||
            t("partners.deals.detail.statusChangeFailed");
          message.error(msg);
        }
      },
    });
  }

  const adoptionFiles = (deal && deal.adoptionFiles) || [];
  const adoptionOkDisabled = adoptionFilesPending.length === 0 && adoptionFiles.length === 0;
  // 교체 권한: 도입 처리한 관리자 본인만 (서버 403 가드와 일치)
  const canManageAdoptionFiles =
    !!deal && !!deal.adoptedByAdminId && String(deal.adoptedByAdminId) === String(profile.userId);
  const showAdoptionSection = !!deal && (deal.status === "adoption" || adoptionFiles.length > 0);

  // '도입' 전환 확정 — multipart 전용 엔드포인트, 응답 후 서버 기준 재조회
  async function confirmAdoption() {
    if (adoptionSubmitting) return;
    setAdoptionSubmitting(true);
    try {
      await adoptAdminDeal(id, { files: adoptionFilesPending });
      setAdoptionOpen(false);
      setAdoptionFilesPending([]);
      message.success(t("partners.deals.detail.statusChanged"));
      try {
        const d = await getAdminDeal(id);
        setDeal(d);
      } catch (e) {
        setDeal((d) => ({ ...d, status: "adoption" }));
      }
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        (err && err.message) ||
        t("partners.deals.detail.statusChangeFailed");
      message.error(msg);
    } finally {
      setAdoptionSubmitting(false);
    }
  }
  async function handleAdoptionAdd(files) {
    const res = await uploadAdoptionFiles(id, files);
    setDeal((d) => ({ ...d, adoptionFiles: (res && res.adoptionFiles) || d.adoptionFiles }));
  }
  async function handleAdoptionRemove(file) {
    const res = await removeAdoptionFile(id, file.id);
    setDeal((d) => ({ ...d, adoptionFiles: (res && res.adoptionFiles) || d.adoptionFiles }));
  }
  function handleAdoptionDownload(file) {
    return downloadAdoptionFile(id, file.id, file.originalName);
  }

  // 삭제 — Modal.confirm 으로 사용자 동의 받은 후 소프트 삭제 호출.
  // 성공 시 어드민 대시보드로 이동.
  function handleDelete() {
    // 삭제 사유(선택) — 다이얼로그 textarea 입력을 클로저 변수로 수집
    let reason = "";
    Modal.confirm({
      title: t("partners.deals.detail.deleteConfirmTitle"),
      content: (
        <div>
          <p>{t("partners.deals.detail.deleteConfirmContent")}</p>
          <textarea
            className="mt-3 w-full rounded-lg border border-slate-300 p-2 text-sm"
            rows={3}
            maxLength={500}
            placeholder={t("partners.deals.detail.deleteReasonPlaceholder")}
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: t("partners.deals.detail.deleteConfirmOk"),
      cancelText: t("partners.deals.detail.deleteConfirmCancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        if (deleting) return;
        setDeleting(true);
        try {
          await removeAdminDeal(id, reason.trim());
          message.success(t("partners.deals.detail.deleteDone"));
          backToList();
        } catch (err) {
          const msg =
            (err && err.payload && err.payload.error && err.payload.error.message) ||
            t("partners.deals.detail.deleteFailed");
          message.error(msg);
        } finally {
          setDeleting(false);
        }
      },
    });
  }

  // 조회 effect 는 t 를 ref 로 읽는다 — react-i18next 는 언어가 바뀔 때마다 t 를 새로 만들어,
  // 의존성에 두면 언어 전환만으로 재조회 → setForm 이 편집 중인 입력을 서버 값으로 덮는다.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAdminDeal(id)
      .then((d) => {
        if (cancelled) return;
        // 삭제된 딜은 수정 폼 대신 읽기 전용 상세로 — 저장 시 서버 404(is_deleted=0 필터)라
        // 편집 가능해 보이는 혼란 방지 (URL 직접 진입 케이스)
        if (d.isDeleted) {
          navigate(`/partners/deals/${id}`, { replace: true });
          return;
        }
        // 딜은 등록한 본인만 수정 가능 — 서버 PUT 가 created_by_admin_id !== 본인 인 경우
        // 거부하지만(파트너 딜·타 관리자 딜), 폼이 노출되는 자체가 혼란을 준다.
        // 담당자 변경으로 파트너사에 넘긴 관리자 등록 딜 — 이제 파트너 딜이라 읽기 전용 상세로 보낸다
        if (!d.isAdminCreated && d.createdByAdminId) {
          navigate(`/partners/deals/${id}`, { replace: true });
          return;
        }
        if (!d.isAdminCreated) {
          message.error(tRef.current("admin.deals.editForbiddenPartnerDeal"));
          backToList();
          return;
        }
        if (String(d.createdByAdminId) !== String(profile.userId)) {
          message.error(tRef.current("admin.deals.editForbiddenNotOwner"));
          backToList();
          return;
        }
        setDeal(d);
        setForm(dealToForm(d));
      })
      .catch(() => {
        if (cancelled) return;
        message.error(tRef.current("admin.deals.loadFailed"));
        backToList();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // profile.userId 의존: 세션이 비워지면(로그아웃/탭 동기화) effect 재실행되어 소유권 재확인 → 리다이렉트(의도).
  }, [id, backToList, profile.userId, navigate]);

  async function handleSubmit(ev) {
    ev.preventDefault();
    if (submitting) return;
    if (
      !form.projectName ||
      !form.customerCompany ||
      !form.customerContactName ||
      !form.adminSalesRepId
    ) {
      message.warning(t("partners.signup.errors.required"));
      return;
    }
    setSubmitting(true);
    try {
      await updateAdminDeal(id, {
        ...form,
        poExpectedQuarter: quarterFromDate(form.poExpectedDate),
      });
      message.success(t("admin.deals.updated"));
      backToList();
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("admin.deals.updateFailed");
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <div className="mx-auto max-w-4xl">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {t("admin.deals.editTitle")}
              </h1>
              {/* 담당자 변경 — 파트너사로 이관하면 파트너 딜이 되어 이 화면에서 더 수정하지 않는다 */}
              {!loading && deal && deal.status !== "adoption" && (
                <button
                  type="button"
                  className="btw-btn-ghost"
                  onClick={() => setOwnerModalOpen(true)}
                >
                  {t("admin.deals.ownerChange.button")}
                </button>
              )}
            </div>
            {loading ? (
              <p className="mt-8 text-center text-sm text-slate-400">{t("common.loading")}</p>
            ) : (
              <>
                {/* 영업단계 변경 — 파트너 딜 상세와 동일한 버튼 그리드. 현재 단계 강조 */}
                <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("partners.deals.detail.adminStatus")}
                  </h2>
                  <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-7">
                    {SALES_STAGES.map((s) => {
                      const isCurrent = deal?.status === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => handleStatusChange(s)}
                          className={`truncate rounded-lg border px-2 py-2 text-xs font-medium transition sm:text-sm ${
                            isCurrent
                              ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                              : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                          }`}
                        >
                          {t(`partners.deals.status.${s}`)}
                        </button>
                      );
                    })}
                  </div>
                </section>

                {/* 도입 증빙 파일 — 도입 상태이거나 파일이 남아 있으면 표시. 교체는 도입 처리 본인만. */}
                {showAdoptionSection && (
                  <AdoptionFilesSection
                    className="mt-6"
                    files={adoptionFiles}
                    canManage={canManageAdoptionFiles}
                    isAdoptionStage={deal.status === "adoption"}
                    onDownload={handleAdoptionDownload}
                    onAdd={handleAdoptionAdd}
                    onRemove={handleAdoptionRemove}
                    limits={deal.adoptionFileLimits}
                  />
                )}

                <AdminDealForm
                  form={form}
                  setForm={setForm}
                  adminCompany={deal?.createdByAdminCompanyName || ""}
                  representativeName={deal?.createdByAdminRepresentativeName || ""}
                  submitting={submitting}
                  onSubmit={handleSubmit}
                  cancelTo="/admin"
                  onCancel={backToList}
                  submitLabel={t("partners.deals.form.submit")}
                  excludeDealId={id}
                  dealId={id}
                  quotation={{
                    hasQuotation: deal?.hasQuotation,
                    quotationOriginalName: deal?.quotationOriginalName,
                  }}
                />

                {/* 변경 이력 — 관리자 등록 딜은 본 페이지에서 수정하므로 여기에 표시.
                    (파트너 등록 딜은 /partners/deals/:id 의 PartnerDealDetail 에 표시) */}
                <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("admin.deals.changeHistory.title")}
                  </h2>
                  <div className="mt-3">
                    <DealChangeHistory dealId={id} />
                  </div>
                </section>

                {/* 위험 영역 — 페이지 최하단. 삭제는 비가역이라 시각적으로 격리. */}
                <section className="mt-6 rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-5 md:p-7">
                  <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-rose-700">
                    {t("partners.deals.detail.dangerZone")}
                  </h2>
                  <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="text-sm text-slate-700">
                      <p className="font-medium text-slate-900">
                        {t("partners.deals.detail.deleteSectionTitle")}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {t("partners.deals.detail.deleteSectionHint")}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/40 disabled:opacity-60"
                      disabled={deleting}
                      onClick={handleDelete}
                    >
                      {t("partners.deals.detail.delete")}
                    </button>
                  </div>
                </section>
              </>
            )}
          </div>
        </Container>
      </main>
      <Footer />

      {/* '도입' 전환 다이얼로그 — 증빙 파일 피커. 파일 0개(기존도 없음)면 확인 비활성 */}
      <Modal
        open={adoptionOpen}
        title={t("partners.deals.detail.statusChangeConfirmTitle")}
        okText={t("partners.deals.detail.statusChangeConfirmOk")}
        cancelText={t("partners.deals.detail.statusChangeConfirmCancel")}
        onOk={confirmAdoption}
        onCancel={() => !adoptionSubmitting && setAdoptionOpen(false)}
        okButtonProps={{ disabled: adoptionOkDisabled, loading: adoptionSubmitting }}
        cancelButtonProps={{ disabled: adoptionSubmitting }}
        closable={!adoptionSubmitting}
        maskClosable={!adoptionSubmitting}
        destroyOnClose
      >
        {deal && (
          <>
            <p className="text-sm text-slate-700">
              {t("partners.deals.detail.statusChangeConfirmContent", {
                from: t(`partners.deals.status.${deal.status}`),
                to: t("partners.deals.status.adoption"),
              })}
            </p>
            <div className="mt-4">
              <label className="mb-1 block text-xs font-medium text-slate-700">
                {t("partners.deals.detail.adoptionFilesLabel")}
              </label>
              <p className="mb-2 text-xs text-slate-500">
                {adoptionFiles.length > 0
                  ? t("partners.deals.detail.adoptionExistingHint", {
                      count: adoptionFiles.length,
                    })
                  : t("partners.deals.detail.adoptionRequiredHint")}
              </p>
              <AdoptionFilePicker
                files={adoptionFilesPending}
                onChange={setAdoptionFilesPending}
                onError={(m) => message.error(m)}
                existingCount={adoptionFiles.length}
                limits={deal.adoptionFileLimits}
                disabled={adoptionSubmitting}
              />
            </div>
          </>
        )}
      </Modal>
      <DealOwnerChangeModal
        open={ownerModalOpen}
        deal={deal}
        onClose={() => setOwnerModalOpen(false)}
        // 관리자 등록 딜을 파트너사로 넘기면 파트너 딜이 된다 — 읽기 전용 상세로 이동
        onChanged={() => {
          setOwnerModalOpen(false);
          navigate(`/partners/deals/${id}`, { replace: true });
        }}
      />
    </div>
  );
}
